import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Breadcrumb } from '@silvertours/common-landingpages-view';
import { Ui } from '@silvertours/front-shared';
import { LayoutLegacy } from '@silvertours/front-legacy-entities';

import {
  BreadcrumbsContentContainer,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbText,
  ScrollContainer,
  ArrowButton,
  InnerBox,
  StyledArrowNext,
  StyledArrowLast,
} from './Breadcrumbs.styles';

type BreadcrumbItemProps = {
  breadcrumb: Breadcrumb;
  isLast: boolean;
  index: number;
};

const BreadcrumbListItem = ({
  breadcrumb: { name, url },
  isLast,
  index,
}: BreadcrumbItemProps) => {
  const shouldRenderText = isLast || !url;

  return (
    <BreadcrumbItem
      itemProp="itemListElement"
      itemScope
      itemType="https://schema.org/ListItem"
    >
      {shouldRenderText ? (
        <BreadcrumbText
          isLast={isLast}
          aria-current={isLast ? 'page' : undefined}
          itemProp={isLast ? 'name' : 'item'}
        >
          {isLast ? name : <span itemProp="name">{name}</span>}
        </BreadcrumbText>
      ) : (
        <Ui.TextLink href={url} itemProp="item">
          <span itemProp="name">{name}</span>
        </Ui.TextLink>
      )}
      <meta itemProp="position" content={String(index + 1)} />
    </BreadcrumbItem>
  );
};

type BreadcrumbsProps = {
  breadcrumbs: Array<Breadcrumb> | null;
};

const Breadcrumbs = ({ breadcrumbs }: BreadcrumbsProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const checkArrows = useCallback(() => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;

      setShowLeftArrow(scrollLeft > 5);
      setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 5);
    }
  }, []);

  useEffect(() => {
    if (breadcrumbs && breadcrumbs.length > 0) {
      checkArrows();
      window.addEventListener('resize', checkArrows);

      const observer = new MutationObserver(checkArrows);
      if (scrollRef.current) {
        observer.observe(scrollRef.current, { childList: true, subtree: true });
      }

      return () => {
        window.removeEventListener('resize', checkArrows);
        observer.disconnect();
      };
    }

    return undefined;
  }, [breadcrumbs, checkArrows]);

  if (!breadcrumbs || breadcrumbs.length === 0) {
    return null;
  }

  const handleScroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const container = scrollRef.current;
      const items = Array.from(container.querySelectorAll('li'));
      const containerRightEdge = container.scrollLeft + container.clientWidth;

      let targetScroll: number;

      if (direction === 'right') {
        const nextItem = items.find(
          item => item.offsetLeft + item.offsetWidth > containerRightEdge + 5,
        );

        if (nextItem) {
          targetScroll = nextItem.offsetLeft;
        } else {
          targetScroll = container.scrollWidth - container.clientWidth;
        }
      } else {
        const prevItem = [...items]
          .reverse()
          .find(item => item.offsetLeft < container.scrollLeft - 5);

        if (prevItem) {
          targetScroll = prevItem.offsetLeft;
        } else {
          targetScroll = 0;
        }
      }

      container.scrollTo({ left: targetScroll, behavior: 'smooth' });
    }
  };

  return (
    <LayoutLegacy.PlainSegment id="breadcrumbs">
      <BreadcrumbsContentContainer>
        <ArrowButton
          side="left"
          visible={showLeftArrow}
          onClick={() => handleScroll('left')}
          aria-label="Zurück"
          type="button"
        >
          <InnerBox side="left">
            <StyledArrowLast />
          </InnerBox>
        </ArrowButton>

        <ScrollContainer ref={scrollRef} onScroll={checkArrows}>
          <BreadcrumbList
            itemScope
            itemType="https://schema.org/BreadcrumbList"
          >
            {breadcrumbs.map((breadcrumb, index) => (
              <BreadcrumbListItem
                key={breadcrumb.url || breadcrumb.name}
                isLast={index === breadcrumbs.length - 1}
                breadcrumb={breadcrumb}
                index={index}
              />
            ))}
          </BreadcrumbList>
        </ScrollContainer>

        <ArrowButton
          side="right"
          visible={showRightArrow}
          onClick={() => handleScroll('right')}
          aria-label="Weiter"
          type="button"
        >
          <InnerBox side="right">
            <StyledArrowNext />
          </InnerBox>
        </ArrowButton>
      </BreadcrumbsContentContainer>
    </LayoutLegacy.PlainSegment>
  );
};

export { Breadcrumbs };
export type { BreadcrumbsProps };
